import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { LinkExternal } from "@/components/LinkExternal";
import { useProgramId, useProgramTheme, useVerificationResponse } from "@/lib/state/contextHooks";
import { getFaqLink, getPrivacyPolicyUrl } from "@/lib/ProgramTheme/programThemeGetters";
import { standardizeLocale } from "@/lib/intl/intl";
import { CLOUDFLARE_PRIVACY_POLICY_URL, FEEDBACK_FORM_URL, SUPPORT_URL } from "@/constants";
import { type Locale } from "@/lib/types/types";

function MessageLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { href, children } = props;
  if (!URL.canParse(href)) return <>{children}</>;

  const url = new URL(href);
  // Limited set of supported protocols
  if (!["http:", "https:", "tel:", "mailto:"].includes(url.protocol)) return <>{children}</>;

  // We will only support a target "_top"; in all other cases use "_blank"
  const target = props.target === "_top" ? "_top" : "_blank";

  return (
    <a
      href={url.toString()}
      target={target}
      rel={target === "_top" ? "noopener noreferrer" : undefined}
      className="sid-link"
    >
      {React.Children.count(children) > 0 ? children : url.toString()}
    </a>
  );
}

function AboutSheerID(props) {
  const { locale, children } = props;
  const programId = useProgramId();
  const programTheme = useProgramTheme();
  const verificationResponse = useVerificationResponse();
  const faqHref = getFaqLink(programTheme, undefined, programId, locale, verificationResponse);

  return (
    <LinkExternal href={faqHref} className="sid-faq sid-link">
      {children}
    </LinkExternal>
  );
}

function PrivacyPolicyLink({
  privacyPolicyHref,
  children,
}: {
  privacyPolicyHref: string;
  children?: React.ReactElement[];
}) {
  if (!privacyPolicyHref) return children;
  return (
    <LinkExternal href={privacyPolicyHref} className="sid-privacy-policy sid-link">
      {children}
    </LinkExternal>
  );
}

function CloudflarePrivacyPolicyLink({ children }: { children?: React.ReactElement[] }) {
  return (
    <LinkExternal href={CLOUDFLARE_PRIVACY_POLICY_URL} className="sid-privacy-policy sid-link">
      {children}
    </LinkExternal>
  );
}

function FeedbackFormLink({
  programId,
  locale,
  children,
}: {
  programId: string;
  locale: Locale;
  children?: React.ReactElement[];
}) {
  return (
    <LinkExternal
      href={`${FEEDBACK_FORM_URL}&pid=${programId}&locale=${standardizeLocale(locale)}`}
      className="sid-link"
    >
      {children}
    </LinkExternal>
  );
}

function TipsLink({
  type,
  locale,
  children,
}: {
  type: "selfie" | "image";
  locale: Locale;
  children?: React.ReactElement[];
}) {
  return (
    <MessageLink
      href={`${SUPPORT_URL}/${standardizeLocale(locale)}/help-center/${type}-tips`}
      className="sid-link"
    >
      {children}
    </MessageLink>
  );
}

function InlineChildrenOnly({ children }: { children?: React.ReactElement[] }) {
  // fragment required as <Trans> components must be typed as ReactElement and not string.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

function UnsupportedBlockElement() {
  // TODO: Update this with a translation for "element not supported"
  // e.g. return <b>[Tables are not supported.]</b>;
  return null;
}

function UnsupportedInlineElement() {
  // TODO: Update this with a translation for "element not supported"
  // return <b>[Tables are not supported.]</b>;
  return null;
}

export function Message({ id, context }: { id: string; context?: string }) {
  const { i18n, t } = useTranslation();
  const programId = useProgramId();
  const programTheme = useProgramTheme();
  const verificationResponse = useVerificationResponse();
  const { verificationId } = verificationResponse;
  const locale = i18n.language as Locale;

  const values = {
    programId,
    verificationId,
    locale,
    companyName: t("companyName"),
  };

  return (
    <Trans
      i18nKey={id}
      context={context}
      t={t}
      components={{
        // DSL tags
        aboutSheerID: <AboutSheerID />,
        cloudflarePrivacyPolicy: <CloudflarePrivacyPolicyLink />,
        feedbackFormLink: <FeedbackFormLink programId={programId || ""} locale={locale} />,
        privacyPolicy: <PrivacyPolicyLink privacyPolicyHref={getPrivacyPolicyUrl(programTheme)} />,
        selfieTipsLink: <TipsLink type="selfie" locale={locale} />,
        imageTipsLink: <TipsLink type="image" locale={locale} />,
        // basic HTML tags
        a: <MessageLink />,
        // prettier-ignore
        br: <><br/><br/></>,
        b: <strong />,
        strong: <strong />,
        i: <em />,
        em: <em />,
        code: <InlineChildrenOnly />,
        span: <span />,
        p: <p />,
        ul: <ul />,
        ol: <ol />,
        li: <li />,
        u: <InlineChildrenOnly />,
        // unsupport CommonMark tags - Inline elements
        img: <UnsupportedInlineElement />,
        // unsupport CommonMark tags - Block elements
        h1: <div />,
        h2: <div />,
        h3: <div />,
        h4: <div />,
        h5: <div />,
        h6: <div />,
        hr: <UnsupportedBlockElement />,
        pre: <div />,
        blockquote: <div />,
        table: <UnsupportedBlockElement />,
      }}
      values={values}
    />
  );
}
